import clsx from 'clsx'
import React, { useEffect } from 'react'

import customImageComponent from '../../../../../components/common/CustomImageComponent'
import { TextField } from './TextField'

import { IconDelete2 } from '../../../../../components/icons/IconDelete2'
import { REQUIRED_FIELDS } from '../../../../../const/required-fields-const'
import {
  getHorseProfileReqField,
  getUserProfileReqField,
  hasInvalidRequiredField,
} from '../../../../../helpers/requiredFields'

import { IHorseCompetitorDocument } from '../../../../../models/horse-competiton-paperwork/horse-competiton-paperwork-interface'
import { IHorseData } from '../../../../../models/horse/horse.interface'
import { IUserPublicInterface } from '../../../../../models/users-public/users-public.interface'

interface ProfileFieldsProps {
  headerTitle: string
  headerSubTitle?: string
  headerImgSrc: string
  editHandler: (data: IProfileReqField, horse?: IHorseData) => void
  user?: IUserPublicInterface
  teamTabDataWithUser?: IUserPublicInterface[]
  setTeamTabDataWithUser?: (value: IUserPublicInterface[]) => void
  myHorses?: IHorseData[]
  horse?: IHorseData
  eventRequiredFields: string[]
  horsePaperwork?: IHorseCompetitorDocument | null
  removeHandler: () => void
  isDeleteBtnHide?: boolean
}

export type IReqFieldType =
  | 'text'
  | 'date'
  | 'select'
  | 'number'
  | 'paperwork'
  | 'tracking-number'
  | 'document'
  | 'tel'

export interface IProfileReqField {
  title: string
  logo: string
  type: IReqFieldType
  fieldKey: string
  fieldValue: string
  fieldUrl?: string
  isExpired?: boolean
  expiredDate?: string
}

export const ProfileFields: React.FC<ProfileFieldsProps> = ({
  headerTitle,
  headerSubTitle,
  headerImgSrc,
  eventRequiredFields,
  user,
  horse,
  editHandler,
  horsePaperwork,
  teamTabDataWithUser,
  setTeamTabDataWithUser,
  myHorses,
  removeHandler,
  isDeleteBtnHide,
}) => {
  const [requiredField, setRequiredField] = React.useState<IProfileReqField[]>([])
  const [errorsCount, setErrorsCount] = React.useState(0)
  const [isOpen, setIsOpen] = React.useState(false)

  useEffect(() => {
    const fields: IProfileReqField[] = []
    let _errors = 0

    if (horse?.id) {
      const requiredFiled = [
        ...REQUIRED_FIELDS.horseMain.filter((itm) => eventRequiredFields.includes(itm.value)),
        ...REQUIRED_FIELDS.horseSecondary.filter((itm) => eventRequiredFields.includes(itm.value)),
        ...REQUIRED_FIELDS.horseNumbers.filter((itm) => eventRequiredFields.includes(itm.value)),
      ]
      requiredFiled.forEach((field) => {
        const profileReqField = getHorseProfileReqField({
          horse,
          field,
          horsePaperwork: horsePaperwork?.id ? horsePaperwork : null,
        })
        if (hasInvalidRequiredField(profileReqField)) _errors++
        fields.push(profileReqField)
      })
    }

    if (user?.id) {
      const requiredFiled = [
        ...REQUIRED_FIELDS.people.filter((itm) => eventRequiredFields.includes(itm.value)),
        ...REQUIRED_FIELDS.peopleNumbers.filter((itm) => eventRequiredFields.includes(itm.value)),
      ]

      requiredFiled.forEach((field) => {
        const profileReqField = getUserProfileReqField({ user, field })
        if (hasInvalidRequiredField(profileReqField)) _errors++
        fields.push(profileReqField)
      })
    }

    setErrorsCount(_errors)
    setRequiredField(fields)
  }, [eventRequiredFields, teamTabDataWithUser, myHorses, user, horse, horsePaperwork])

  return (
    <div className="relative border border-[#D3DAEE] w-full rounded-lg p-1">
      <div className="relative flex w-full gap-4 items-center">
        {customImageComponent(
          headerImgSrc,
          headerTitle,
          'w-[45px] h-[45px] object-cover border rounded'
        )}
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="cursor-pointer flex flex-col gap-0.5 pr-8"
        >
          <h4 className="text-sm">{headerTitle}</h4>
          <div className="flex flex-wrap items-center gap-1">
            <p className="text-xs text-SeabiscuitDark200ThemeColor/50">{headerSubTitle}</p>
            {!errorsCount && requiredField[0] && (
              <p className="text-xs text-SeabiscuitDark200ThemeColor/50">
                • <span className="underline hover:no-underline">Edit Profile</span>
              </p>
            )}
            {!!errorsCount && (
              <p className="text-xs text-SeabiscuitRedThemeColor">
                • <span className="underline hover:no-underline">Fix {errorsCount} errors</span>
              </p>
            )}
          </div>
        </div>
        {!isDeleteBtnHide && (
          <button
            className="transition-all opacity-50 hover:opacity-100 absolute z-10 right-1 top-1/2 -translate-y-1/2"
            onClick={removeHandler}
          >
            <IconDelete2 />
          </button>
        )}
      </div>
      {isOpen && requiredField[0] && (
        <div className="flex flex-col mt-5 gap-2">
          {requiredField.map((itm, index) => (
            <div
              key={index}
              className={clsx(
                'h-auto lg:min-h-[55px] rounded-lg flex flex-wrap items-center justify-between gap-3 p-3',
                hasInvalidRequiredField(itm)
                  ? 'bg-[#E800000D] text-[#E80000]'
                  : 'bg-[#F6F7FB] text-[#122B46]'
              )}
            >
              <p>{itm.title}</p>
              <TextField
                isHorse={!!horse?.id}
                horseId={horse?.id}
                userId={user?.id}
                onSave={({ fieldValue }) => {
                  const newRequiredFields = [...requiredField]
                  newRequiredFields[index] = {
                    ...requiredField[index],
                    fieldValue: fieldValue,
                  }
                  setRequiredField(newRequiredFields)
                  setErrorsCount((v) => v - 1)
                }}
                field={itm}
                editHandler={editHandler}
                horse={horse}
                teamTabDataWithUser={teamTabDataWithUser}
                setTeamTabDataWithUser={setTeamTabDataWithUser}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
